// src/utils/logger.ts
//
// Centralized browser logger. Never use console.* directly in src/.
// ESLint enforces no-console: error in src/ — this file is the only allowed exception.
/* eslint-disable no-console */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_RANK: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

function resolveLevel(): LogLevel {
  try {
    const fromQuery = new URLSearchParams(self.location.search).get('LOGLEVEL');
    const fromEnv = import.meta.env.VITE_LOGLEVEL as string | undefined;
    const raw = (fromQuery ?? fromEnv ?? 'info').toLowerCase();
    return (raw in LEVEL_RANK ? raw : 'info') as LogLevel;
  } catch {
    return 'info';
  }
}

const CURRENT_LEVEL = resolveLevel();

function shouldLog(level: LogLevel): boolean {
  return LEVEL_RANK[level] >= LEVEL_RANK[CURRENT_LEVEL];
}

function format(level: LogLevel, scope: string, msg: string): string {
  const ts = new Date().toISOString();
  return `[${ts}] [${level.toUpperCase()}] [${scope}] ${msg}`;
}

export interface Logger {
  debug(msg: string, ...args: unknown[]): void;
  info(msg: string, ...args: unknown[]): void;
  warn(msg: string, ...args: unknown[]): void;
  error(msg: string, ...args: unknown[]): void;
}

export function createLogger(scope: string): Logger {
  return {
    debug(msg, ...args) {
      if (shouldLog('debug')) {
        console.debug(format('debug', scope, msg), ...args);
      }
    },
    info(msg, ...args) {
      if (shouldLog('info')) {
        console.info(format('info', scope, msg), ...args);
      }
    },
    warn(msg, ...args) {
      if (shouldLog('warn')) {
        console.warn(format('warn', scope, msg), ...args);
      }
    },
    error(msg, ...args) {
      if (shouldLog('error')) {
        console.error(format('error', scope, msg), ...args);
      }
    },
  };
}
